/**
 * RegistrationsWidget - Виджет регистраций
 * =========================================
 * 
 * Полностью переписан с использованием новой архитектуры
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { MetricsService } from '../../services/metricsService';
import { formatChartDate, formatTooltipDate, getXAxisConfig } from '../../utils/chartUtils';
import { Card, CardContent } from '../ui/card';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { TrendingUp, TrendingDown, Users, Calendar, Activity, BarChart3, LineChart as LineChartIcon, AreaChart as AreaChartIcon } from 'lucide-react';
import { users as usersSpecs } from '@ggstat/chart-specs';

const REGISTRATIONS_COLOR = usersSpecs.registrations.webColor;

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, change, trend }) {
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (trend === 'down') return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };
  
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className="text-xl font-bold">{MetricsService.formatNumber(value)}</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            {getTrendIcon()}
            {change !== undefined && (
              <span className={`text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Компонент графика (без анимации)
 */
function RegistrationsChart({ data, chartType = 'line', groupBy = 'day' }) {
  const xAxisConfig = getXAxisConfig(groupBy, data.length);
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload[0]) {
      const formattedLabel = formatTooltipDate(label, groupBy);
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">{formattedLabel}</p>
          <p className="text-sm text-primary">
            Registrations: {MetricsService.formatNumber(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };
  
  if (chartType === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatNumber(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="registrations" 
            fill={REGISTRATIONS_COLOR}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }
  
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="period"
            tickFormatter={(value) => formatChartDate(value, groupBy)}
            {...xAxisConfig}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: 'currentColor' }}
            tickFormatter={(value) => MetricsService.formatNumber(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="registrations" 
            stroke={REGISTRATIONS_COLOR}
            fill={REGISTRATIONS_COLOR}
            fillOpacity={0.3}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  
  // Default: line chart
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="period"
          tickFormatter={(value) => formatChartDate(value, groupBy)}
          {...xAxisConfig}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          tickFormatter={(value) => MetricsService.formatNumber(value)}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line 
          type="monotone" 
          dataKey="registrations" 
          stroke={REGISTRATIONS_COLOR}
          strokeWidth={2}
          dot={{ fill: REGISTRATIONS_COLOR, r: 4 }}
          activeDot={{ r: 6 }}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Основной компонент виджета
 */
export function RegistrationsWidget() {
  // Используем localStorage для сохранения настроек
  const [groupBy, setGroupBy] = useLocalStorage('registrations-widget-groupBy', 'day');
  const [chartType, setChartType] = useLocalStorage('registrations-widget-chartType', 'line');
  
  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      const response = await MetricsService.getRegistrations(filters, groupBy, options);
      return response;
    },
    [groupBy] // Перезагружаем при изменении группировки
  );
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  const { data: registrationsData } = widgetProps;
  
  // Вычисляем изменение
  const percentChange = React.useMemo(() => {
    if (!registrationsData?.data || registrationsData.data.length < 2) return 0;
    
    const data = registrationsData.data;
    const midPoint = Math.floor(data.length / 2);
    const firstHalf = data.slice(0, midPoint);
    const secondHalf = data.slice(midPoint);
    
    const firstAvg = firstHalf.reduce((sum, d) => sum + d.registrations, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, d) => sum + d.registrations, 0) / secondHalf.length;
    
    return MetricsService.calculatePercentChange(secondAvg, firstAvg);
  }, [registrationsData]);
  
  // Компоненты выбора настроек
  const Controls = () => (
    <div className="flex flex-wrap items-center gap-2">
      <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value)}>
        <ToggleGroupItem value="day" aria-label="Daily">
          <Calendar className="h-4 w-4 mr-1" />
          Daily
        </ToggleGroupItem>
        <ToggleGroupItem value="week" aria-label="Weekly">
          <Calendar className="h-4 w-4 mr-1" />
          Weekly
        </ToggleGroupItem>
        <ToggleGroupItem value="month" aria-label="Monthly">
          <Calendar className="h-4 w-4 mr-1" />
          Monthly
        </ToggleGroupItem>
      </ToggleGroup>
      
      <div className="h-6 w-px bg-border" />
      
      <ToggleGroup type="single" value={chartType} onValueChange={(value) => value && setChartType(value)}>
        <ToggleGroupItem value="line" aria-label="Line Chart">
          <LineChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="bar" aria-label="Bar Chart">
          <BarChart3 className="h-4 w-4" />
        </ToggleGroupItem>
        <ToggleGroupItem value="area" aria-label="Area Chart">
          <AreaChartIcon className="h-4 w-4" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
  
  return (
    <BaseWidget
      {...widgetProps}
      title="Registrations Analytics"
      subtitle={registrationsData?.total ? `Total: ${MetricsService.formatNumber(registrationsData.total)}` : null}
      headerAction={<Controls />}
      emptyMessage="No registrations data available"
      skeletonRows={6}
    >
      <div className="space-y-6">
        {/* Статистика */}
        {registrationsData?.summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Total"
              value={registrationsData.total}
              change={percentChange}
              trend={registrationsData.summary.trend}
            />
            <StatCard
              icon={Activity}
              label="Average"
              value={registrationsData.summary.average}
              trend={registrationsData.summary.trend}
            />
            <StatCard
              icon={TrendingUp}
              label="Maximum"
              value={registrationsData.summary.max}
            />
            <StatCard
              icon={Calendar}
              label="Minimum"
              value={registrationsData.summary.min}
            />
          </div>
        )}
        
        {/* График */}
        {registrationsData?.data && registrationsData.data.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Registration Trend</h4>
            <RegistrationsChart 
              data={registrationsData.data} 
              chartType={chartType}
              groupBy={groupBy}
            />
          </div>
        )}
      </div>
    </BaseWidget>
  );
}

export default RegistrationsWidget;
